// Browsers call several of our API servers on their own hostnames ("direct")
// instead of through Cloudflare, because Cloudflare cuts requests at 100s and
// some of these calls run longer. Some networks cannot reach those direct
// servers at all (school and corporate filters, some ISPs, security software)
// while the same servers work for them through Cloudflare. In Sep 2026 PostHog
// showed ~150 people in three weeks whose editor, template library or AI
// generation never connected, all failing in milliseconds, all on direct hosts.
//
// Each direct host browsers use has a Cloudflare-fronted twin on codedesign.ai.
// createOriginFallback() sends a request to its remembered route first and, if
// that route cannot be reached at all, to the other one; the route that worked
// is remembered for the session. Hosts without a twin are never touched.
// Pure logic, no browser or app dependencies, so it can be unit-tested.

export const ORIGIN_TWINS = Object.freeze({
    'https://ai-companion-beta.codedesign.app': 'https://ai-companion-beta.codedesign.ai',
    'https://playground.codedesign.app': 'https://playground.codedesign.ai',
    'https://site-audit-api.codedesign.app': 'https://site-audit-api.codedesign.ai',
    'https://production-api.codedesign.app': 'https://production-api.codedesign.ai',
})

export const ROUTE_KEY = 'cd_origin_routes'

// A request that fails later than this may already have reached the server (for
// example a long generation whose connection dropped). It is replayed only when a
// probe shows its route is down and the other route answers.
export const NEVER_CONNECTED_MS = 15000

// How long a live-connection fallback waits to confirm the other route answers.
export const PROBE_TIMEOUT_MS = 4000

/** "https://host" for a URL (http is normalised to https), or null. */
export function originOf(url) {
    const match = /^https?:\/\/([^/?#]+)/i.exec(String(url || ''))
    return match ? `https://${match[1].toLowerCase()}` : null
}

const swapOrigin = (url, origin) => String(url).replace(/^https?:\/\/[^/?#]+/i, origin)
const pathOf = (url) => String(url).replace(/^https?:\/\/[^/?#]+/i, '').split('?')[0] || '/'

export function createOriginFallback({
    storage = null,
    fetchImpl,
    track = null,
    now = () => Date.now(),
    twins = ORIGIN_TWINS,
    eventName = 'Api:OriginFallback',
} = {}) {
    const readRoutes = () => {
        try {
            return JSON.parse(storage?.getItem(ROUTE_KEY) || '{}') || {}
        } catch {
            return {}
        }
    }

    const writeRoute = (origin, route) => {
        try {
            const routes = readRoutes()
            if (route === 'twin') routes[origin] = 'twin'
            else delete routes[origin]
            storage?.setItem(ROUTE_KEY, JSON.stringify(routes))
        } catch {
            /* storage unavailable: fall back per request instead */
        }
    }

    const report = (details) => {
        try {
            track?.(eventName, details)
        } catch {
            /* analytics must never affect the request */
        }
    }

    const twinOf = (origin) => (origin && twins[origin]) || null
    const onTwin = (origin) => readRoutes()[origin] === 'twin'

    /** The base URL to use now for `base` (a direct host or anything else). */
    const preferredOrigin = (base) => {
        const origin = originOf(base)
        const twin = twinOf(origin)
        return twin && onTwin(origin) ? swapOrigin(base, twin) : base
    }

    /** Same as preferredOrigin, for a full URL (iframes, links). */
    const preferredUrl = preferredOrigin

    const isRequest = (input) => input && typeof input === 'object' && !(input instanceof URL) && typeof input.url === 'string'

    const send = (input, init, url) => (isRequest(input) ? fetchImpl(new Request(url, input), init) : fetchImpl(url, init))

    async function fetchWithFallback(input, init) {
        const url = isRequest(input) ? input.url : String(input)
        const origin = originOf(url)
        const twin = twinOf(origin)
        if (!twin) return fetchImpl(input, init)

        const twinUrl = swapOrigin(url, twin)
        const [firstRoute, firstUrl, secondRoute, secondUrl] = onTwin(origin)
            ? ['twin', twinUrl, 'direct', url]
            : ['direct', url, 'twin', twinUrl]
        // A Request body can be read once; keep a copy for the second attempt.
        const spare = isRequest(input) && typeof input.clone === 'function' ? input.clone() : input

        const startedAt = now()
        try {
            return await send(input, init, firstUrl)
        } catch (error) {
            if (error?.name === 'AbortError') throw error
            if (now() - startedAt >= NEVER_CONNECTED_MS) {
                // A slow failure may have reached the server (a long generation whose
                // connection dropped), so it is replayed only when its route is down
                // now and the other route answers. Some networks drop packets to a
                // direct host instead of refusing them, so every request hung ~30 s,
                // failed, and never fell back (joshuatempleton06@gmail.com, Oct 2026).
                const [firstUp, secondUp] = await Promise.all([
                    reachable(firstRoute === 'twin' ? twin : origin),
                    reachable(secondRoute === 'twin' ? twin : origin),
                ])
                if (firstUp || !secondUp) throw error
            }
            let response
            try {
                response = await send(spare, init, secondUrl)
            } catch {
                // Neither route works right now: surface the original error and
                // keep the remembered route as it was.
                throw error
            }
            writeRoute(origin, secondRoute)
            report({
                origin,
                from: firstRoute,
                to: secondRoute,
                path: pathOf(url),
                message: String(error?.message || error).slice(0, 120),
            })
            return response
        }
    }

    // Any response at all (even an error page) means the route is reachable.
    // no-cors: reachability must not depend on the probing page's CORS rules.
    async function reachable(base, timeoutMs = PROBE_TIMEOUT_MS) {
        const controller = typeof AbortController === 'function' ? new AbortController() : null
        const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null
        try {
            await fetchImpl(`${String(base).replace(/\/+$/, '')}/`, {
                method: 'GET',
                mode: 'no-cors',
                cache: 'no-store',
                credentials: 'omit',
                signal: controller?.signal,
            })
            return true
        } catch {
            return false
        } finally {
            if (timer) clearTimeout(timer)
        }
    }

    /**
     * A live connection (socket.io / WebSocket) to `base` never reached the
     * server on its current route. Switches to the other route only if that
     * route answers, and resolves to the base to reconnect to; resolves to null
     * (route unchanged) when `base` has no twin or the other route is down too.
     */
    async function switchRouteAfterSocketFailure(base) {
        const origin = originOf(base)
        const twin = twinOf(origin)
        if (!twin) return null
        const next = onTwin(origin) ? 'direct' : 'twin'
        if (!(await reachable(next === 'twin' ? twin : origin))) return null
        writeRoute(origin, next)
        report({ origin, from: next === 'twin' ? 'direct' : 'twin', to: next, path: 'socket', message: 'live connection could not be established' })
        return preferredOrigin(base)
    }

    return { fetchWithFallback, preferredOrigin, preferredUrl, switchRouteAfterSocketFailure, twinOf }
}

/**
 * Wraps target.fetch so every request to a direct host gets the fallback,
 * including code written later. Idempotent. Returns the client (or null when
 * there is no fetch to wrap).
 */
export function installGlobalFetchFallback({ target = globalThis, storage = null, track = null, eventName } = {}) {
    if (!target || typeof target.fetch !== 'function') return null
    if (target.__cdOriginFallback) return target.__cdOriginFallback
    const original = target.fetch.bind(target)
    const client = createOriginFallback({ storage, fetchImpl: original, track, eventName })
    target.fetch = (input, init) => client.fetchWithFallback(input, init)
    target.__cdOriginFallback = client
    return client
}
